<template>
  <div class="radio-app">
    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
    </div>

    <!-- Main Glass Container -->
    <div class="glass-container">

      <!-- Header Controls -->
      <div class="header-controls">
        <!-- Country Selector -->
        <v-dialog v-model="countryDialog" max-width="400">
          <template v-slot:activator="{ props }">
            <button class="glass-btn" v-bind="props">
              <v-icon>mdi-earth</v-icon>
            </button>
          </template>
          <div class="glass-dialog">
            <h3 class="dialog-title">Select Country</h3>
            <v-text-field v-model="countrySearch" label="Search country" prepend-inner-icon="mdi-magnify"
              variant="outlined" density="compact" class="search-field" clearable />
            <div class="country-list">
              <div class="country-item featured" @click="selectCountry('it')">
                <img src="https://flagcdn.com/w40/it.png" alt="Italy" />
                <span>Italia</span>
                <v-icon v-if="selectedCountry === 'it'" color="primary">mdi-check</v-icon>
              </div>
              <v-divider class="my-2" />
              <div v-for="country in filteredCountries" :key="country.code" class="country-item"
                @click="selectCountry(country.code)">
                <img :src="`https://flagcdn.com/w40/${country.code}.png`" :alt="country.name" />
                <span>{{ country.name }}</span>
                <v-icon v-if="selectedCountry === country.code" color="primary">mdi-check</v-icon>
              </div>
            </div>
          </div>
        </v-dialog>

        <!-- Favorites -->
        <v-dialog v-model="favoritesDialog" max-width="400">
          <template v-slot:activator="{ props }">
            <button class="glass-btn" v-bind="props">
              <v-icon :color="favorites.length > 0 ? 'red-lighten-1' : 'white'">mdi-heart</v-icon>
            </button>
          </template>
          <div class="glass-dialog">
            <h3 class="dialog-title">Favorites</h3>
            <div v-if="favorites.length > 0" class="favorites-list">
              <div v-for="station in favorites" :key="station.stationuuid" class="favorite-item"
                @click="playFavorite(station)">
                <img :src="station.favicon || defaultCover" alt="Station" class="station-avatar" />
                <div class="station-info">
                  <div class="station-name">{{ station.name }}</div>
                  <div class="station-country">{{ station.country }}</div>
                </div>
                <button class="delete-btn" @click.stop="removeFavorite(station)">
                  <v-icon size="small">mdi-delete</v-icon>
                </button>
              </div>
            </div>
            <div v-else class="empty-state">No favorites yet</div>
            <button v-if="favorites.length > 0" class="clear-all-btn" @click="confirmClearAll">
              Clear All
            </button>
          </div>
        </v-dialog>

        <!-- Station List -->
        <v-dialog v-model="stationsDialog" max-width="400">
          <template v-slot:activator="{ props }">
            <button class="glass-btn" v-bind="props">
              <v-icon>mdi-playlist-music</v-icon>
            </button>
          </template>
          <div class="glass-dialog">
            <h3 class="dialog-title">Radio Stations</h3>
            <v-text-field v-model="stationSearch" label="Search station" prepend-inner-icon="mdi-magnify"
              variant="outlined" density="compact" class="search-field" clearable @input="filterStations" />
            <div class="stations-list">
              <div v-for="(station, index) in filteredStationsList" :key="station.stationuuid" class="station-item"
                :class="{ active: currentStation?.stationuuid === station.stationuuid }"
                @click="switchStationFromFiltered(index)">
                <img :src="station.favicon || defaultCover" alt="Station" class="station-avatar" />
                <div class="station-info">
                  <div class="station-name">{{ station.name }}</div>
                  <div class="station-meta">{{ station.country }} • {{ station.bitrate }}kbps</div>
                </div>
                <button class="fav-btn" @click.stop="toggleFavorite(station)">
                  <v-icon :color="station.favorite ? 'red-lighten-1' : 'white'" size="small">mdi-heart</v-icon>
                </button>
              </div>
            </div>
          </div>
        </v-dialog>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <v-progress-circular indeterminate color="primary" size="64" />
        <p>Loading stations...</p>
      </div>

      <!-- Player -->
      <div v-else-if="currentStation" class="player">
        <!-- Logo -->
        <div class="logo-container">
          <img src="logo.png" alt="Logo" class="logo" />
        </div>

        <!-- Album Art -->
        <div class="album-art-container">
          <div class="album-art" :style="{ backgroundImage: `url(${getImage(currentStation.favicon)})` }">
            <div v-if="isPlaying" class="pulse-ring"></div>
          </div>
        </div>

        <!-- Station Info -->
        <div class="station-details">
          <h2 class="station-title">{{ currentStation.name }}</h2>

          <!-- Visualizer -->
          <div class="visualizer-container">
            <img v-if="isPlaying" src="img/wave.gif" alt="Playing" class="wave-animation" />
          </div>

          <!-- Now Playing -->
          <div class="now-playing" :key="currentStation?.stationuuid">
            <div v-if="nowPlayingTitle" class="marquee">
              <span class="marquee-text">{{ nowPlayingTitle }}</span>
            </div>
            <div v-else class="station-subtitle">{{ stationSubtitle }}</div>
          </div>
        </div>

        <!-- Controls -->
        <div class="controls">
          <button class="control-btn" @click="prevStation">
            <v-icon size="32">mdi-skip-previous</v-icon>
          </button>
          <button class="control-btn play-btn" @click="play">
            <v-icon size="48">{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
          </button>
          <button class="control-btn" @click="nextStation">
            <v-icon size="32">mdi-skip-next</v-icon>
          </button>
        </div>

        <!-- Volume Control -->
        <div class="volume-control">
          <v-icon size="20">mdi-volume-high</v-icon>
          <v-slider v-model="volume" min="0" max="100" step="1" hide-details color="primary"
            @update:model-value="updateVolume" />
          <span class="volume-label">{{ volume }}%</span>
        </div>
      </div>

      <!-- Footer -->
      <div class="footer">
        <p>{{ new Date().getFullYear() }} — <strong>RadioWebRadio</strong> - <a href="https://www.gabriarcu.it"
            style="color: white;">Gabriarcu</a>
        </p>
      </div>
    </div>

    <!-- PWA Install Snackbar -->
    <transition name="slide-up">
      <div v-if="showInstallPrompt" class="install-snackbar">
        <div class="snackbar-content">
          <v-icon size="24" color="white" class="snackbar-icon">mdi-download</v-icon>
          <div class="snackbar-text">
            <strong>Installa l'app</strong>
            <span>Aggiungi alla home screen per un accesso rapido</span>
          </div>
          <button class="install-btn" @click="installApp">Installa</button>
          <button class="close-btn" @click="dismissInstallPrompt">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import Hls from 'hls.js';
import { nowPlaying } from './metadata.js';
import { startTitleRotation, stopTitleRotation } from '@/utils/updateTitle';

// State
const audio = ref(null);
const hls = ref(null);
const isPlaying = ref(false);
const isLoading = ref(true);
const countriesLoading = ref(true);
const stations = ref([]);
const stationsList = ref([]);
const countries = ref([]);
const selectedCountry = ref('it');
const currentStation = ref(null);
const currentStationIndex = ref(0);
const favorites = ref(JSON.parse(localStorage.getItem('favoriteStations')) || []);
const nowPlayingTitle = ref(null);
const metadataSubscription = ref(null);
const metadataInterval = ref(null);
const volume = ref(70);

// Search
const countrySearch = ref('');
const stationSearch = ref('');
const filteredStationsList = ref([]);

// Dialogs
const countryDialog = ref(false);
const favoritesDialog = ref(false);
const stationsDialog = ref(false);

// PWA Install
const showInstallPrompt = ref(false);
const deferredPrompt = ref(null);

// Constants
const defaultCover = 'https://media.istockphoto.com/id/506176142/photo/vintage-radio.jpg?s=612x612&w=0&k=20&c=n-0-n5h72nyj8zbCqCxRoSWfsCXct0Ski-mA9ZRa__c=';
const apiUrl = 'https://de2.api.radio-browser.info/json/stations/search';

// Computed
const stationSubtitle = computed(() => {
  if (!currentStation.value) return '';
  return [
    currentStation.value.tags,
    currentStation.value.country,
    currentStation.value.bitrate ? `${currentStation.value.bitrate}kbps` : ''
  ].filter(Boolean).join(' • ');
});

const filteredCountries = computed(() => {
  if (!countrySearch.value) return countries.value;
  const searchTerm = countrySearch.value.toLowerCase();
  return countries.value.filter(country =>
    country.name.toLowerCase().includes(searchTerm) ||
    country.code.toLowerCase().includes(searchTerm)
  );
});

// Watchers
watch(currentStation, (newStation) => {
  if (newStation) {
    // Forza l'aggiornamento con null per resettare i metadati
    startTitleRotation(newStation.name, null);
  } else {
    stopTitleRotation();
    document.title = 'Radio Player';
  }
});

watch(nowPlayingTitle, (newTitle) => {
  if (currentStation.value) {
    startTitleRotation(currentStation.value.name, newTitle);
  }
});

watch(stationsList, (newVal) => {
  filteredStationsList.value = [...newVal];
});

// Methods
const filterStations = () => {
  if (!stationSearch.value) {
    filteredStationsList.value = [...stationsList.value];
    return;
  }
  const searchTerm = stationSearch.value.toLowerCase();
  filteredStationsList.value = stationsList.value.filter(station =>
    station.name.toLowerCase().includes(searchTerm) ||
    (station.country && station.country.toLowerCase().includes(searchTerm)) ||
    (station.tags && station.tags.toLowerCase().includes(searchTerm))
  );
};

const startMetadataTracking = () => {
  stopMetadataTracking();
  if (!currentStation.value?.url_resolved) return;

  metadataSubscription.value = nowPlaying.subscribe(({ title, error }) => {
    if (error) {
      console.error('Metadata error:', error);
      return;
    }
    nowPlayingTitle.value = formatMetadata(title);
    const metadata = formatMetadata(title);
    startTitleRotation(currentStation.value.name, metadata || '');
  });

  metadataInterval.value = setInterval(() => {
    nowPlaying.trackStream(currentStation.value.url_resolved);
  }, 5000);

  nowPlaying.trackStream(currentStation.value.url_resolved);
};

const stopMetadataTracking = () => {
  if (metadataSubscription.value) {
    metadataSubscription.value.unsubscribe();
    metadataSubscription.value = null;
  }
  if (metadataInterval.value) {
    clearInterval(metadataInterval.value);
    metadataInterval.value = null;
  }
  nowPlayingTitle.value = null;
};

const startPlayback = () => {
  if (!currentStation.value) return;

  if (hls.value) {
    hls.value.destroy();
  }

  const url = currentStation.value.url_resolved;

  if (url.includes('m3u8')) {
    hls.value = new Hls();
    hls.value.loadSource(url);
    hls.value.attachMedia(audio.value);
    hls.value.on(Hls.Events.MANIFEST_PARSED, () => {
      audio.value.play();
      startMetadataTracking();
    });
  } else {
    audio.value.src = url;
    audio.value.play();
    startMetadataTracking();
  }
};

const stopPlayback = () => {
  audio.value.pause();
  if (hls.value) {
    hls.value.destroy();
  }
  stopMetadataTracking();
  startTitleRotation(currentStation.value?.name || 'Radio Player', '');
};

const play = () => {
  if (!audio.value.paused) {
    stopPlayback();
    isPlaying.value = false;
  } else {
    startPlayback();
    isPlaying.value = true;
  }
  startTitleRotation(currentStation.value?.name || 'Radio Player', nowPlayingTitle.value || '');
};

const prevStation = () => {
  let newIndex = currentStationIndex.value - 1;
  if (newIndex < 0) newIndex = stations.value.length - 1;
  switchStationByIndex(newIndex);
};

const nextStation = () => {
  let newIndex = currentStationIndex.value + 1;
  if (newIndex >= stations.value.length) newIndex = 0;
  switchStationByIndex(newIndex);
};

const switchStationByIndex = (index) => {
  currentStationIndex.value = index;
  currentStation.value = stations.value[index];
  stopMetadataTracking();
  nowPlayingTitle.value = null; // Reset metadata display

  if (isPlaying.value) {
    stopPlayback();
    startPlayback();
  }
};

const switchStationFromFiltered = (filteredIndex) => {
  const filteredStation = filteredStationsList.value[filteredIndex];
  const originalIndex = stations.value.findIndex(
    s => s.stationuuid === filteredStation.stationuuid
  );

  if (originalIndex !== -1) {
    switchStationByIndex(originalIndex);
  }
  stationsDialog.value = false;
};

const fetchCountryCodes = async () => {
  try {
    const response = await fetch('https://flagcdn.com/it/codes.json');
    const data = await response.json();
    countries.value = Object.entries(data)
      .map(([code, name]) => ({ code: code.toLowerCase(), name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('Error fetching countries:', error);
  } finally {
    countriesLoading.value = false;
  }
};

const fetchStations = async (countryCode = 'IT') => {
  try {
    isLoading.value = true;
    const params = new URLSearchParams({
      countrycode: countryCode,
      hidebroken: 'true',
      reverse: 'true',
      order: 'clickcount',
      limit: '300',
    });

    const response = await fetch(`${apiUrl}?${params}`);
    if (!response.ok) throw new Error('Network response was not ok');

    let stationsData = await response.json();

    stations.value = stationsData.filter(station => {
      if (!station.url_resolved) return false;
      return station.url_resolved.startsWith('https://') ||
        station.url_resolved.toLowerCase().includes('icecast') ||
        station.url_resolved.toLowerCase().endsWith('.mp3');
    });

    stationsList.value = [...stations.value];

    if (stations.value.length > 0) {
      currentStation.value = stations.value[0];
      preloadFavicons();
    }
  } catch (error) {
    console.error('Error fetching stations:', error);
    stations.value = [];
    stationsList.value = [];
  } finally {
    isLoading.value = false;
  }
};

const preloadFavicons = () => {
  stations.value.forEach(station => {
    if (station.favicon) {
      const link = document.createElement('link');
      link.rel = "prefetch";
      link.href = station.favicon;
      link.as = "image";
      document.head.appendChild(link);
    }
  });
};

const selectCountry = (code) => {
  selectedCountry.value = code;
  fetchStations(code.toUpperCase());
  countryDialog.value = false;
};

const toggleFavorite = (station) => {
  const index = favorites.value.findIndex(f => f.stationuuid === station.stationuuid);

  if (index === -1) {
    station.favorite = true;
    favorites.value.push({
      stationuuid: station.stationuuid,
      name: station.name,
      url_resolved: station.url_resolved,
      favicon: station.favicon,
      country: station.country
    });
  } else {
    station.favorite = false;
    favorites.value.splice(index, 1);
  }

  localStorage.setItem('favoriteStations', JSON.stringify(favorites.value));
};

const playFavorite = (station) => {
  const index = stations.value.findIndex(s => s.stationuuid === station.stationuuid);
  if (index !== -1) {
    currentStationIndex.value = index;
    currentStation.value = stations.value[index];
    if (!isPlaying.value) {
      play();
    } else {
      stopPlayback();
      startPlayback();
    }
  }
  favoritesDialog.value = false;
};

const removeFavorite = (station) => {
  const index = favorites.value.findIndex(fav => fav.stationuuid === station.stationuuid);
  if (index !== -1) {
    favorites.value.splice(index, 1);
    const mainIndex = stations.value.findIndex(s => s.stationuuid === station.stationuuid);
    if (mainIndex !== -1) {
      stations.value[mainIndex].favorite = false;
    }
    localStorage.setItem('favoriteStations', JSON.stringify(favorites.value));
  }
};

const confirmClearAll = () => {
  if (confirm('Are you sure you want to clear all favorites?')) {
    clearAllFavorites();
  }
};

const clearAllFavorites = () => {
  stations.value.forEach(station => {
    station.favorite = false;
  });
  favorites.value = [];
  localStorage.setItem('favoriteStations', JSON.stringify(favorites.value));
};

const getImage = (favicon) => {
  return favicon || defaultCover;
};

const formatMetadata = (rawTitle) => {
  if (!rawTitle) {
    stopTitleRotation();
    return null;
  }

  const parts = rawTitle.split('~');
  const title = parts[0]?.trim();
  const artist = parts[1]?.trim();
  const year = parts[3]?.trim();

  return [title, artist, year && `(${year})`].filter(Boolean).join(' - ');
};

const updateVolume = (value) => {
  if (audio.value) {
    audio.value.volume = value / 100;
  }
};

// PWA Install functions
const installApp = async () => {
  if (!deferredPrompt.value) {
    // Se non c'è il prompt nativo, mostra istruzioni
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    let message = '';
    if (isIOS) {
      message = 'Per installare su iOS:\n1. Tocca il pulsante Condividi\n2. Scorri e tocca "Aggiungi a Home"';
    } else if (isMobile) {
      message = 'Per installare:\n1. Apri il menu del browser (⋮)\n2. Seleziona "Aggiungi a schermata Home"';
    } else {
      message = 'Per installare:\n1. Clicca sull\'icona di installazione nella barra degli indirizzi\nOppure:\n2. Menu browser → "Installa app"';
    }

    alert(message);
    return;
  }

  deferredPrompt.value.prompt();
  const { outcome } = await deferredPrompt.value.userChoice;

  if (outcome === 'accepted') {
    console.log('User accepted the install prompt');
  }

  deferredPrompt.value = null;
  showInstallPrompt.value = false;
};

const dismissInstallPrompt = () => {
  showInstallPrompt.value = false;
  localStorage.setItem('installPromptDismissed', 'true');
};

// Lifecycle
onMounted(async () => {
  audio.value = new Audio();
  audio.value.volume = volume.value / 100;

  await fetchCountryCodes();
  await fetchStations();

  stations.value.forEach(station => {
    station.favorite = favorites.value.some(f => f.stationuuid === station.stationuuid);
  });

  filteredStationsList.value = [...stationsList.value];

  // PWA Install prompt
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt.value = e;

    // Mostra lo snackbar solo se non è stato dismesso prima
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (!dismissed) {
      setTimeout(() => {
        showInstallPrompt.value = true;
      }, 3000); // Mostra dopo 3 secondi
    }
  });

  // Per testare in sviluppo, mostra lo snackbar anche senza PWA
  const dismissed = localStorage.getItem('installPromptDismissed');
  if (!dismissed) {
    setTimeout(() => {
      showInstallPrompt.value = true;
    }, 3000);
  }
});

onBeforeUnmount(() => {
  stopPlayback();
  stopTitleRotation();
  stopMetadataTracking();
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.radio-app {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  overflow: hidden;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* Animated Background */
.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #012a4a 0%, #013a63 50%, #01497c 100%);
  z-index: -1;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.6;
  animation: float 20s ease-in-out infinite;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #2a6f97 0%, #2c7da0 100%);
  top: -100px;
  left: -100px;
  animation-delay: 0s;
}

.orb-2 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #468faf 0%, #61a5c2 100%);
  bottom: -100px;
  right: -100px;
  animation-delay: 7s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #89c2d9 0%, #a9d6e5 100%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 14s;
}

@keyframes float {

  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  33% {
    transform: translate(50px, -50px) scale(1.1);
  }

  66% {
    transform: translate(-50px, 50px) scale(0.9);
  }
}

/* Glass Container */
.glass-container {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(25px) saturate(180%);
  border-radius: 35px;
  border: 1.5px solid rgba(255, 255, 255, 0.18);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset,
    0 0 60px rgba(44, 125, 160, 0.15);
  padding: 40px;
  max-width: 450px;
  width: 100%;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.glass-container:hover {
  box-shadow:
    0 12px 48px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.15) inset,
    0 0 80px rgba(44, 125, 160, 0.25);
  transform: translateY(-2px);
}

/* Header Controls */
.header-controls {
  display: flex;
  justify-content: space-around;
  gap: 15px;
  margin-bottom: 30px;
}

.glass-btn {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  color: white;
  position: relative;
  overflow: hidden;
}

.glass-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.glass-btn:hover::before {
  width: 100px;
  height: 100px;
}

.glass-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-3px) scale(1.05);
  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.4),
    0 0 20px rgba(44, 125, 160, 0.4);
  border-color: rgba(44, 125, 160, 0.5);
}

.glass-btn:active {
  transform: translateY(-1px) scale(0.98);
}

/* Glass Dialog */
.glass-dialog {
  background: rgba(30, 30, 50, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 25px;
  color: white;
  max-height: 500px;
  overflow-y: auto;
}

.dialog-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #2c7da0 0%, #61a5c2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.search-field {
  margin-bottom: 15px;
}

/* Country List */
.country-list {
  max-height: 300px;
  overflow-y: auto;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.03);
  margin-bottom: 8px;
}

.country-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(5px);
}

.country-item.featured {
  background: linear-gradient(135deg, rgba(44, 125, 160, 0.2) 0%, rgba(97, 165, 194, 0.2) 100%);
  border: 1px solid rgba(44, 125, 160, 0.3);
}

.country-item img {
  width: 30px;
  height: 20px;
  border-radius: 4px;
}

.country-item span {
  flex: 1;
  font-size: 16px;
}

/* Stations List */
.stations-list {
  max-height: 350px;
  overflow-y: auto;
}

.station-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.03);
  margin-bottom: 8px;
}

.station-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(5px);
}

.station-item.active {
  background: linear-gradient(135deg, rgba(44, 125, 160, 0.3) 0%, rgba(97, 165, 194, 0.3) 100%);
  border-left: 4px solid #2c7da0;
}

.station-avatar {
  width: 45px;
  height: 45px;
  border-radius: 10px;
  object-fit: cover;
}

.station-info {
  flex: 1;
  min-width: 0;
}

.station-name {
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.station-meta,
.station-country {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

.fav-btn,
.delete-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.fav-btn:hover,
.delete-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Favorites List */
.favorites-list {
  max-height: 300px;
  overflow-y: auto;
}

.favorite-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.03);
  margin-bottom: 8px;
}

.favorite-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(5px);
}

.clear-all-btn {
  width: 100%;
  margin-top: 15px;
  padding: 12px;
  background: rgba(244, 67, 54, 0.2);
  border: 1px solid rgba(244, 67, 54, 0.4);
  border-radius: 12px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
}

.clear-all-btn:hover {
  background: rgba(244, 67, 54, 0.3);
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 60px 20px;
  color: white;
}

/* Player */
.player {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-container {
  margin-bottom: 20px;
}

.logo {
  width: 280px !important;
  max-width: 90% !important;
  height: auto !important;
  opacity: 0.9;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
}

/* Album Art */
.album-art-container {
  position: relative;
  margin: 30px 0;
  perspective: 1000px;
}

.album-art {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  box-shadow:
    0 15px 50px rgba(0, 0, 0, 0.6),
    0 0 0 3px rgba(255, 255, 255, 0.1),
    0 0 40px rgba(44, 125, 160, 0.3);
  border: 5px solid rgba(255, 255, 255, 0.15);
  position: relative;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
}

.album-art:hover {
  transform: scale(1.08) rotateY(5deg);
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.7),
    0 0 0 3px rgba(255, 255, 255, 0.2),
    0 0 60px rgba(44, 125, 160, 0.5);
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid rgba(44, 125, 160, 0.6);
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {

  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }

  100% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0;
  }
}

/* Station Details */
.station-details {
  text-align: center;
  margin-bottom: 30px;
  width: 100%;
}

.station-title {
  font-size: 24px;
  font-weight: 600;
  color: white;
  margin-bottom: 15px;
  background: linear-gradient(90deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 1) 50%,
      rgba(255, 255, 255, 0.9) 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: shimmer 3s linear infinite;
}

@keyframes shimmer {
  0% {
    background-position: 0% center;
  }

  100% {
    background-position: 200% center;
  }
}

.visualizer-container {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.wave-animation {
  width: 150px;
  height: auto;
}

.now-playing {
  min-height: 30px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.marquee {
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
}

.marquee-text {
  display: inline-block;
  animation: marquee 15s linear infinite;
  padding-left: 100%;
}

@keyframes marquee {

  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-100%);
  }
}

.station-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
}

/* Controls */
.controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 25px;
}

.control-btn {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  color: white;
  position: relative;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.15) translateY(-2px);
  box-shadow:
    0 8px 25px rgba(0, 0, 0, 0.4),
    0 0 25px rgba(44, 125, 160, 0.4);
  border-color: rgba(44, 125, 160, 0.6);
}

.control-btn:active {
  transform: scale(1.05) translateY(0);
}

.play-btn {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #2c7da0 0%, #61a5c2 100%);
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 10px 30px rgba(44, 125, 160, 0.5),
    0 0 40px rgba(44, 125, 160, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  animation: playPulse 2s ease-in-out infinite;
}

@keyframes playPulse {

  0%,
  100% {
    box-shadow:
      0 10px 30px rgba(44, 125, 160, 0.5),
      0 0 40px rgba(44, 125, 160, 0.3),
      0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  }

  50% {
    box-shadow:
      0 10px 30px rgba(44, 125, 160, 0.6),
      0 0 50px rgba(44, 125, 160, 0.5),
      0 0 0 1px rgba(255, 255, 255, 0.15) inset;
  }
}

.play-btn:hover {
  transform: scale(1.15) translateY(-2px);
  box-shadow:
    0 15px 45px rgba(44, 125, 160, 0.7),
    0 0 60px rgba(44, 125, 160, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.2) inset;
  animation: none;
}

/* Volume Control */
.volume-control {
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%;
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  color: white;
}

/* Override aggressivo per il thumb dello slider */
.volume-control :deep(.v-slider-thumb) {
  width: 16px !important;
  height: 16px !important;
  border-radius: 50% !important;
}

.volume-control :deep(.v-slider-thumb__surface) {
  width: 16px !important;
  height: 16px !important;
  border-radius: 50% !important;
}

.volume-control :deep(.v-slider-thumb__ripple) {
  width: 16px !important;
  height: 16px !important;
  border-radius: 50% !important;
}

.volume-control :deep(.v-slider-thumb__label) {
  display: none !important;
}

.volume-control :deep(.v-slider-thumb > *) {
  width: 16px !important;
  height: 16px !important;
  border-radius: 50% !important;
}

.volume-control :deep(.v-slider-track__fill) {
  height: 3px;
}

.volume-control :deep(.v-slider-track__background) {
  height: 3px;
}

.volume-label {
  min-width: 40px;
  text-align: right;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

/* Footer */
.footer {
  margin-top: 30px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
}

.footer strong {
  color: rgba(255, 255, 255, 0.8);
}

/* Scrollbar Styling */
.glass-dialog::-webkit-scrollbar,
.country-list::-webkit-scrollbar,
.stations-list::-webkit-scrollbar,
.favorites-list::-webkit-scrollbar {
  width: 6px;
}

.glass-dialog::-webkit-scrollbar-track,
.country-list::-webkit-scrollbar-track,
.stations-list::-webkit-scrollbar-track,
.favorites-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
}

.glass-dialog::-webkit-scrollbar-thumb,
.country-list::-webkit-scrollbar-thumb,
.stations-list::-webkit-scrollbar-thumb,
.favorites-list::-webkit-scrollbar-thumb {
  background: rgba(44, 125, 160, 0.5);
  border-radius: 10px;
}

.glass-dialog::-webkit-scrollbar-thumb:hover,
.country-list::-webkit-scrollbar-thumb:hover,
.stations-list::-webkit-scrollbar-thumb:hover,
.favorites-list::-webkit-scrollbar-thumb:hover {
  background: rgba(44, 125, 160, 0.7);
}

/* Responsive */
@media (max-width: 600px) {
  .glass-container {
    padding: 25px;
  }

  .album-art {
    width: 180px;
    height: 180px;
  }

  .station-title {
    font-size: 20px;
  }

  .control-btn {
    width: 50px;
    height: 50px;
  }

  .play-btn {
    width: 70px;
    height: 70px;
  }

  .logo {
    width: 150px;
  }
}

/* PWA Install Snackbar */
.install-snackbar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  max-width: 90%;
  width: 500px;
}

.snackbar-content {
  background: rgba(30, 30, 50, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset,
    0 0 30px rgba(44, 125, 160, 0.2);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  color: white;
}

.snackbar-icon {
  flex-shrink: 0;
}

.snackbar-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.snackbar-text strong {
  font-size: 16px;
  font-weight: 600;
}

.snackbar-text span {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}

.install-btn {
  background: linear-gradient(135deg, #2c7da0 0%, #61a5c2 100%);
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(44, 125, 160, 0.4);
}

.install-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(44, 125, 160, 0.6);
}

.close-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

/* Slide up animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(100px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(100px);
}

@media (max-width: 600px) {
  .snackbar-text span {
    display: none;
  }

  .install-snackbar {
    width: calc(100% - 40px);
  }
}
</style>